import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Coins, Gift, Sparkles, CreditCard, ArrowRight } from "lucide-react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toastManager from "../../utils/toastManager";
import CheckoutModal from "../checkout/CheckoutModal";

const QUICK_AMOUNTS = [5, 10, 25, 50];
const MIN_TOPUP_AMOUNT = 1;
const MAX_TOPUP_AMOUNT = 1000;

// Mirrors the worker-side calculation so the preview always matches what gets credited.
export const calculateTopUpPoints = (amount, pointsRate, bonusPercent) => {
  const numericAmount = Number(amount) || 0;
  const rate = Number(pointsRate) || 20;
  const bonus = Math.max(0, Number(bonusPercent) || 0);
  if (numericAmount <= 0) return { basePoints: 0, bonusPoints: 0, totalPoints: 0 };

  const basePoints = Math.round(numericAmount * rate);
  const bonusPoints = Math.round(basePoints * (bonus / 100));
  return { basePoints, bonusPoints, totalPoints: basePoints + bonusPoints };
};

const PointsTopUpModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { settings } = useSelector((state) => state.settings);
  const [amount, setAmount] = useState("10");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutItem, setCheckoutItem] = useState(null);

  const pointsRate = settings?.pointsRate || 20;
  const bonusPercent = Math.max(0, Number(settings?.topUpBonusPercent) || 0);

  const { basePoints, bonusPoints, totalPoints } = useMemo(
    () => calculateTopUpPoints(amount, pointsRate, bonusPercent),
    [amount, pointsRate, bonusPercent]
  );

  const closeAll = () => {
    setCheckoutOpen(false);
    setCheckoutItem(null);
    setAmount("10");
    onClose?.();
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount < MIN_TOPUP_AMOUNT) {
      toastManager.error(t("topup.min_amount_error", { min: MIN_TOPUP_AMOUNT, defaultValue: `Minimum top-up is $${MIN_TOPUP_AMOUNT}` }));
      return;
    }
    if (numericAmount > MAX_TOPUP_AMOUNT) {
      toastManager.error(t("topup.max_amount_error", { max: MAX_TOPUP_AMOUNT, defaultValue: `Maximum top-up is $${MAX_TOPUP_AMOUNT}` }));
      return;
    }
    if (totalPoints <= 0) {
      toastManager.error(t("topup.invalid_amount_error", "Enter a valid amount to continue"));
      return;
    }

    const roundedAmount = Math.round(numericAmount * 100) / 100;

    setCheckoutItem({
      id: `points-topup-${Date.now()}`,
      name: t("topup.item_name", { count: totalPoints, defaultValue: `${totalPoints} Reward Points` }),
      price: roundedAmount,
      image: "",
      category: "points-topup",
      itemType: "points-topup",
      quantity: 1,
      validity: null,
      pointsAmount: totalPoints,
      basePoints,
      bonusPoints,
      bonusPercent,
      pointsRate,
      topUpAmount: roundedAmount
    });
    setCheckoutOpen(true);
  };

  return (
    <AnimatePresence>
      {isOpen && !checkoutOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeAll}
            className="absolute inset-0 bg-gray-950/75 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            className="relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-950"
          >
            <div className="relative overflow-hidden border-b border-amber-100 bg-gradient-to-br from-white via-amber-50 to-orange-50 px-4 py-4 text-gray-950 dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white sm:px-5">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-yellow-400/15 dark:from-amber-500/30 dark:via-orange-500/25 dark:to-yellow-400/10" />
              <div className="relative flex items-start justify-between gap-4">
                <div className="flex min-w-0 gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-amber-100 bg-white/80 text-amber-600 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-white">
                    <Coins className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-white/80 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-amber-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-amber-100">
                      <Gift className="h-3 w-3" />
                      {t("topup.badge", "Top up")}
                    </div>
                    <h3 className="text-xl font-black leading-tight sm:text-2xl">{t("topup.title", "Buy Reward Points")}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                      {t("topup.subtitle", "Top up your balance now to unlock discounts and free scripts across the store.")}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeAll}
                  className="rounded-2xl border border-gray-200 bg-white/80 p-2.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 dark:border-white/10 dark:bg-white/[0.08] dark:text-gray-300 dark:hover:bg-white/15 dark:hover:text-white"
                  aria-label={t("topup.close_aria", "Close top-up form")}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-4 sm:p-5">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-4 gap-2">
                  {QUICK_AMOUNTS.map((quickAmount) => {
                    const isActive = Number(amount) === quickAmount;
                    return (
                      <button
                        key={quickAmount}
                        type="button"
                        onClick={() => setAmount(String(quickAmount))}
                        className={`inline-flex min-h-11 items-center justify-center rounded-2xl border text-sm font-black transition notranslate ${
                          isActive
                            ? "border-amber-500 bg-amber-500 text-white shadow-lg shadow-amber-500/20"
                            : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                        }`}
                        translate="no"
                      >
                        ${quickAmount}
                      </button>
                    );
                  })}
                </div>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-black text-gray-800 dark:text-gray-100">
                    {t("topup.amount_label", "Amount (USDT)")} <span className="text-red-500">*</span>
                  </span>
                  <span className="relative block">
                    <CreditCard className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      name="amount"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value)}
                      min={MIN_TOPUP_AMOUNT}
                      max={MAX_TOPUP_AMOUNT}
                      step="0.01"
                      placeholder="10.00"
                      className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3 pl-10 pr-4 text-sm font-semibold text-gray-950 outline-none transition focus:border-amber-500 focus:bg-white focus:ring-4 focus:ring-amber-500/10 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:focus:border-amber-500"
                      required
                    />
                  </span>
                </label>

                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-gray-600 dark:text-gray-300">
                      {t("topup.base_points", { rate: pointsRate, defaultValue: `Base points (${pointsRate} pts / $1)` })}
                    </span>
                    <span className="font-black text-gray-950 dark:text-white notranslate" translate="no">{basePoints}</span>
                  </div>

                  {bonusPercent > 0 && (
                    <div className="mt-2 flex items-center justify-between text-sm">
                      <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700 dark:text-emerald-300">
                        <Sparkles className="h-4 w-4" />
                        {t("topup.bonus_points", { percent: bonusPercent, defaultValue: `Bonus (${bonusPercent}%)` })}
                      </span>
                      <span className="font-black text-emerald-700 dark:text-emerald-300 notranslate" translate="no">+{bonusPoints}</span>
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between border-t border-amber-200 pt-3 dark:border-amber-500/20">
                    <span className="text-sm font-black text-gray-950 dark:text-white">{t("topup.you_receive", "You receive")}</span>
                    <span className="text-xl font-black text-amber-600 dark:text-amber-400 notranslate" translate="no">
                      {t("topup.pts_count", { count: totalPoints, defaultValue: `${totalPoints} pts` })}
                    </span>
                  </div>
                </div>

                <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  {t("topup.disclaimer", "Reward points cannot be used to pay for a top-up, and coupons do not apply. Points appear in your balance after an admin verifies the payment.")}
                </p>

                <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeAll}
                    className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 text-sm font-black text-gray-700 transition hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    {t("topup.cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-amber-600 px-5 text-sm font-black text-white shadow-lg shadow-amber-500/20 transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t("topup.continue_payment", "Continue to Payment")}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      <CheckoutModal
        isOpen={checkoutOpen}
        onClose={closeAll}
        directItems={checkoutItem ? [checkoutItem] : []}
        directMode="points-topup"
      />
    </AnimatePresence>
  );
};

export default PointsTopUpModal;
